import React, {useState, useEffect, useCallback, useRef} from "react";

// CLASSES
import Settings from "@classes/Settings";

// HELPERS
import api from "@helpers/api";
import i18n from "@helpers/i18n";
import {getCookie} from "@helpers/cookies";
import {createB64Context, stringifyQueryParams, formatSSN} from "@helpers/utils";

// COMPONENTS
import Modal from "@components/Modal/Modal";
import ActivityIndicator from "@components/ActivityIndicator/ActivityIndicator";
import ContentBox from "@components/ContentBox/ContentBox";

import FuturTable from "./FuturTable";

const FuturWithdrawalsView = (props) => {
	const {
		isCustomerModal,
		user,
		advisor,
		partner,
		showHeader,
		onWorkflowSwitched,
		caseFilterContext,
		setCustomerState,
		isAdviceSession,
		nextStepOptions,
		client
	} = props;

	const [isLoading, setIsLoading] = useState(false); // Default to false for placeholder
	const [isLoadingCases, setIsLoadingCases] = useState(false);
	const [currentAction, setCurrentAction] = useState('none');
	const [isDisabled, setIsDisabled] = useState(false);
	const [userCaseList, setUserCaseList] = useState([]);
	const [totalCases, setTotalCases] = useState(0);
	const [error, setError] = useState(false);

	// Helper function to get cases - Placeholder version
	const getWithdrawalsList = useCallback((page, searchString, order, orderBy) => {
		const userSsn = user.ssn;
		const advisorSsn = advisor.ssn;
		const customerSsn = client?.id ? // if a client was passed as a prop (customer modal)
			formatSSN(client.id, {withDash: false})
			: client?.id === "" ? // handle client was passed as a prop but with no ssn set
				""
				: null; // no ssn passed means it's a main dashboard widget not filtered by customer

		// check if we have any options saved..
		const cobOptions = JSON.parse(getCookie("cobOptions")) || null;

		// .. and set rowsPerPage
		const rowsPerPage = cobOptions?.rowsPerPage["futur"] || 10;

		return new Promise(async (resolve, reject) => {
			try
			{
				console.log("Loading list of Futur withdrawals...");
				const cases = await api("/futur/withdrawals", {
					userSsn,
					advisorSsn,
					customerSsn,
					filter: caseFilterContext.caseFilter.futur,
					page: page || 0,
					order: order || "desc",
					orderBy: orderBy || "created",
					searchString: searchString || "",
					limit: rowsPerPage === -1 ? 100 : rowsPerPage
				});

				resolve(cases);
			} catch (e)
			{
				reject(e);
			}
		});
	}, [user.ssn, advisor.ssn, client?.id, caseFilterContext.caseFilter.futur]);


	const isFirstRender = useRef(true);

	// Consolidated Effect for Data Fetching
	useEffect(() => {
		let isMounted = true;

		const fetchData = async () => {
			if (isFirstRender.current)
			{
				setIsLoading(true);
			}
			else
			{
				setIsLoadingCases(true);
			}

			try
			{
				const result = await getWithdrawalsList();
				if (isMounted && result)
				{
					setIsLoading(false);
					setIsLoadingCases(false);
					setTotalCases(parseInt(result.total));
					setUserCaseList(result.batch);
				}
			} catch (err)
			{
				if (isMounted)
				{
					console.error(err);
					setError(true);
					setIsLoading(false);
					setIsLoadingCases(false);
				}
			}
		};

		fetchData();
		isFirstRender.current = false;

		return () => {
			isMounted = false;
		};
	}, [getWithdrawalsList]);


	const handleRowAction = async (action, caseId, ssn, callbackFunc) => {
		console.log("Action on withdrawal row:", action, caseId);
		// Placeholder: No actions implemented yet
	};

	return (
		<>
			<ContentBox style={
				isCustomerModal ?
					{
						minHeight: "66vh",
						margin: 0,
						padding: 0,
						overflow: "auto",
						boxShadow: "none"
					}
					:
					{
						width: "98.5%",
						margin: 0,
						marginBottom: "22.5px"
					}
			}>
				{showHeader && (
					<h2>
						{i18n("dashboard_selector", "futur_withdrawals") || "Futur Återköp"}
					</h2>
				)}
				{isLoading && (
					<div style={{padding: "91px 0"}}>
						<ActivityIndicator color="#4cb7d5" busy/>
					</div>
				)}
				{error && (
					<div style={{padding: "91px 0"}}>Ett fel uppstod när data laddades, kontakta supporten.</div>
				)}
				{!error && !isLoading && (
					<>
						<div className="dashboard-cases"
							 style={
								 isCustomerModal ?
									 {
										 minHeight: "unset"
									 }
									 :
									 {}
							 }>
							<FuturTable
								user={user}
								type="futur_withdrawals"
								partner={partner}
								advisor={advisor}
								data={userCaseList}
								totalCases={totalCases}
								isLoadingCases={isLoadingCases || false}
								currentAction={currentAction}
								whitelabel={partner.name}
								isCustomerModal={isCustomerModal || false}
								caseFilterContext={caseFilterContext}
								setCustomerState={setCustomerState}
								isAdviceSession={isAdviceSession || false}
								nextStepOptions={nextStepOptions}
								onWorkflowSwitched={onWorkflowSwitched}
								onAction={handleRowAction}
								getCases={getWithdrawalsList}
							/>
							{isDisabled && (
								<div className="dashboard-cases-disabled">
									<ActivityIndicator color="#4cb7d5" busy/>
								</div>
							)}
						</div>
					</>
				)}
			</ContentBox>
		</>
	);
};

export default FuturWithdrawalsView;
